/* eslint-disable no-console */
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { connect } from "react-redux";
import { useState, useMemo, useEffect } from "react";
import { newAlertMessage } from "store/actions/globalAlertActions";
import { useNavigate } from "react-router-dom";
import { change as changeFormValue } from "redux-form";
// @mui material components
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import MenuItem from "@mui/material/MenuItem";
import MDInput from "components/MDInput";
import PropTypes from "prop-types";
import moment from "moment";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CardTitle from "components/CardTitle";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import DataTable from "components/DataTable";
import ClientReservationForm from "layouts/clients/components/clientReservationsForm";
import MDAppBar from "components/MDAppBar";

import ClientReservationsTimeline from "layouts/clients/components/ClientReservationsTimeline";
import {
  upsertClientReservation,
  removeClientReservation,
} from "store/actions/clientReservationsActions";
import { upsertClient, removeClient } from "store/actions/clientsActions";

import {
  buildAvailabilityColumns,
  buildAvailabilityRows,
  buildClientTabsInfo,
  buildClientsColumns,
  buildReservationColumns,
  buildReservationRow,
} from "./clientReservationsUtils";
import ClientForm from "./components/clientForm";

const selectSx = {
  "& .MuiInputBase-root": {
    height: 44,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    padding: "10.5px 14px",
  },
};

function AlertDialog({ open, handleClose, handleAgree, type }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <MDBox
        sx={({ palette: { white, background } }) => ({
          backgroundColor: darkMode ? background.sidenav : white.main,
        })}
      >
        <DialogTitle id="alert-dialog-title">{t("confirmation")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {type === "client" ? t("stepNotUndoClient") : t("stepNotUndoClientReservation")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose}>{t("cancel")}</MDButton>
          <MDButton onClick={handleAgree} autoFocus>
            {t("OK")}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

function ClientReservations({
  clientReservations,
  clients,
  projects,
  apartments,
  actUpsertClientReservation,
  actRemoveClientReservation,
  actUpsertClient,
  actSendRemoveClient,
  actChangeClientReservationClient,
  actNewAlertMessage,
}) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [uids, setUids] = useState([]);
  const [actionType, setActionType] = useState("");

  const [selectedProjectId, setSelectedProjectId] = useState("");

  const [selectedStartDateTime, setSelectedStartDateTime] = useState(
    moment().format("YYYY-MM-DDTHH:mm")
  );
  const [selectedEndDateTime, setSelectedEndDateTime] = useState(
    moment().add(1, "day").format("YYYY-MM-DDTHH:mm")
  );

  const [tabValue, setTabValue] = useState(0);

  const [creatingClientFromReservation, setCreatingClientFromReservation] = useState(false);
  const [apartmentIdSelected, setApartmentIdSelected] = useState("");

  const projectOptions = useMemo(
    () => Object.values(projects || {}).filter((project) => Boolean(project?.uid)),
    [projects]
  );
  const hasSingleProject = projectOptions.length === 1;

  useEffect(() => {
    if (hasSingleProject) {
      const singleProjectId = projectOptions[0]?.uid || "";
      if (singleProjectId && selectedProjectId !== singleProjectId) {
        setSelectedProjectId(singleProjectId);
      }
    }
  }, [hasSingleProject, projectOptions, selectedProjectId]);

  const navigate = useNavigate();

  const handleModalClose = () => {
    setCreatingClientFromReservation(false);
    setApartmentIdSelected("");
    if (setOpen) setOpen(false);
    if (setActionType) setActionType("");
    if (setUids) setUids([]);
  };

  const handleCreateClientFromReservation = () => {
    setCreatingClientFromReservation(true);
  };

  const submit = async (data) => {
    if (tabValue === 4) {
      actUpsertClient(data, (resp) => {
        const { ok } = resp || {};

        if (!ok) {
          if (actionType === "add") {
            actNewAlertMessage({
              type: "error",
              title: "client",
              content: "errorCreatingClient",
            });
            return;
          }

          if (actionType === "edit") {
            actNewAlertMessage({
              type: "error",
              title: "client",
              content: "errorUpdatingClient",
            });
            return;
          }
        }

        setActionType("");
        setOpen(false);

        if (actionType === "add") {
          actNewAlertMessage({
            type: "success",
            title: "client",
            content: "clientCreatedSuccessfully",
          });
        } else if (actionType === "edit") {
          actNewAlertMessage({
            type: "success",
            title: "client",
            content: "clientUpdatedSuccessfully",
          });
        }
      });

      return;
    }

    try {
      switch (actionType) {
        case "edit":
        case "add":
          actUpsertClientReservation(
            {
              ...data,
              startDate: moment.isMoment(data.startDate)
                ? data.startDate.valueOf()
                : data.startDate,
              endDate: moment.isMoment(data.endDate) ? data.endDate.valueOf() : data.endDate,
            },
            (resp) => {
              const { ok } = resp || {};
              if (!ok) {
                if (actionType === "add") {
                  actNewAlertMessage({
                    type: "error",
                    title: "clientReservation",
                    content: t("errorCreatingClientReservation"),
                  });
                  return;
                }
                if (actionType === "edit") {
                  actNewAlertMessage({
                    type: "error",
                    title: "clientReservation",
                    content: t("errorUpdatingClientReservation"),
                  });
                  return;
                }
              }

              setActionType("");
              setOpen(false);
              if (actionType === "add") {
                actNewAlertMessage({
                  type: "success",
                  title: "clientReservation",
                  content: t("clientReservationCreatedSuccessfully"),
                });
              }
              if (actionType === "edit") {
                actNewAlertMessage({
                  type: "success",
                  title: "clientReservation",
                  content: t("clientReservationUpdatedSuccessfully"),
                });
              }
            }
          );

          break;

        default:
          break;
      }
    } catch (error) {
      console.error("Error al guardar reserva:", error);
    }

    setActionType("");
    setOpen(false);
  };

  const submitClientFromReservation = (data) => {
    actUpsertClient(data, (resp) => {
      const { ok, client } = resp || {};
      const { uid } = client || {};

      if (!ok || !uid) {
        if (actionType === "add") {
          actNewAlertMessage({
            type: "error",
            title: "client",
            content: "errorCreatingClient",
          });
          return;
        }

        if (actionType === "edit") {
          actNewAlertMessage({
            type: "error",
            title: "client",
            content: "errorUpdatingClient",
          });
          return;
        }
      }

      actChangeClientReservationClient(uid);
      setCreatingClientFromReservation(false);

      actNewAlertMessage({
        type: "success",
        title: "client",
        content: t("clientCreatedSuccessfully"),
      });
    });
  };

  const removeAction = () => {
    if (actionType === "remove") {
      const selectedData = uids[0];
      if (tabValue === 4) {
        actSendRemoveClient(selectedData?.uid, (resp) => {
          const { ok } = resp || {};

          if (!ok) {
            actNewAlertMessage({
              type: "error",
              title: "client",
              content: t("errorRemovingClient"),
            });
            return;
          }

          actNewAlertMessage({
            type: "success",
            title: "client",
            content: t("clientRemovedSuccessfully"),
          });
        });
      } else {
        actRemoveClientReservation(
          { clientId: selectedData?.clientId, uid: selectedData?.uid },
          (resp) => {
            const { ok } = resp || {};

            if (!ok) {
              actNewAlertMessage({
                type: "error",
                title: "clientReservation",
                content: t("errorRemovingClientReservation"),
              });
              return;
            }

            actNewAlertMessage({
              type: "success",
              title: "clientReservation",
              content: t("clientReservationRemovedSuccessfully"),
            });
          }
        );
      }
      setOpenConfirm(false);
    }
  };

  const add = () => {
    setUids([]);
    setCreatingClientFromReservation(false);
    setApartmentIdSelected("");
    setActionType("add");
    setOpen(true);
  };

  const edit = (indexes) => {
    const ids = [];

    indexes.forEach((row) => {
      const { uid } = row.original;
      ids.push(uid);
    });

    setCreatingClientFromReservation(false);
    setUids(ids);
    setActionType("edit");
    setOpen(true);
  };

  const remove = (indexes) => {
    const items = [];
    indexes.forEach((row) => {
      items.push(row.original);
    });

    setActionType("remove");
    setUids(items);
    setOpenConfirm(true);
  };

  const tabsInfo = buildClientTabsInfo(t);

  const titleToShow = () => tabsInfo?.[tabValue]?.label;

  const columns = useMemo(() => {
    switch (tabValue) {
      case 0:
        return buildAvailabilityColumns(t);
      case 1:
        return buildReservationColumns(t);
      case 2:
        return buildReservationColumns(t);
      case 4:
        return buildClientsColumns(t);
      default:
        return [];
    }
  }, [t, tabValue]);

  const rows = useMemo(() => {
    if (tabValue === 4) {
      return Object.values(clients || {});
    }

    if (tabValue === 0) {
      const selectedStartTimestamp = selectedStartDateTime
        ? moment(selectedStartDateTime).valueOf()
        : NaN;
      const selectedEndTimestamp = selectedEndDateTime
        ? moment(selectedEndDateTime).valueOf()
        : NaN;

      return buildAvailabilityRows({
        projects,
        apartments,
        clientReservations,
        selectedProjectId,
        selectedStartTimestamp,
        selectedEndTimestamp,
        t,
      });
    }

    const selectedTimestamp =
      tabValue === 1 && selectedStartDateTime
        ? moment(selectedStartDateTime).valueOf()
        : Date.now();

    const formatedClientReservations = (clientReservations || [])
      .map((reservation) =>
        buildReservationRow(reservation, {
          clients,
          projects,
          apartments,
          now: selectedTimestamp,
        })
      )
      .filter(Boolean);

    if (tabValue === 1) {
      const activeFormatedClientReservations = formatedClientReservations.filter((reservation) => {
        if (!reservation?.active) return false;
        if (selectedProjectId && reservation?.projectId !== selectedProjectId) {
          return false;
        }
        return true;
      });

      return activeFormatedClientReservations;
    }

    if (tabValue === 2) {
      return formatedClientReservations;
    }

    return [];
  }, [
    tabValue,
    clientReservations,
    clients,
    projects,
    apartments,
    selectedProjectId,
    selectedStartDateTime,
    selectedEndDateTime,
  ]);

  return (
    <DashboardLayout>
      <AlertDialog
        open={openConfirm}
        handleClose={() => {
          setOpenConfirm(false);
        }}
        handleAgree={() => {
          removeAction();
        }}
        type={tabValue === 4 ? "client" : "clientReservation"}
      />

      <Modal
        open={open}
        onClose={handleModalClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{ overflow: "scroll" }}
      >
        <MDBox
          mt={3}
          sx={12}
          sm={8}
          style={{
            position: "absolute",
            width: "65vw",
            left: "50%",
            transform: "translate(-50%, 0%)",
            outline: "none",
          }}
        >
          {tabValue === 4 && <ClientForm onSubmit={submit} actionType={actionType} uids={uids} />}

          {(tabValue === 0 || tabValue === 1 || tabValue === 2) && (
            <MDBox>
              <MDBox display={creatingClientFromReservation ? "none" : "block"}>
                <ClientReservationForm
                  onSubmit={submit}
                  actionType={actionType}
                  uids={uids}
                  fixedProjectId={
                    actionType === "add" && (tabValue === 0 || tabValue === 1)
                      ? selectedProjectId
                      : ""
                  }
                  fixedApartmentId={
                    actionType === "add" && tabValue === 0 ? apartmentIdSelected : ""
                  }
                  fixedStartDate={
                    actionType === "add" && tabValue === 0 && selectedStartDateTime
                      ? moment(selectedStartDateTime).valueOf()
                      : undefined
                  }
                  fixedEndDate={
                    actionType === "add" && tabValue === 0 && selectedEndDateTime
                      ? moment(selectedEndDateTime).valueOf()
                      : undefined
                  }
                  disableProjectSelector={
                    actionType === "add" &&
                    (tabValue === 0 || tabValue === 1) &&
                    !!selectedProjectId
                  }
                  disableApartmentSelector={
                    actionType === "add" && tabValue === 0 && !!apartmentIdSelected
                  }
                  onCreateClient={handleCreateClientFromReservation}
                />
              </MDBox>

              {creatingClientFromReservation && (
                <ClientForm onSubmit={submitClientFromReservation} actionType="add" uids={[]} />
              )}
            </MDBox>
          )}
        </MDBox>
      </Modal>

      <DashboardNavbar title={`${t("clientReservations")}`} route="/clientReservations" />

      <MDBox mt={3}>
        <MDAppBar
          tabsInfo={tabsInfo}
          tabValue={tabValue}
          setTabValue={(indexValue) => setTabValue(indexValue)}
        />
      </MDBox>

      <MDBox pt={4} pb={3}>
        {(tabValue === 4 || tabValue === 0 || tabValue === 1 || tabValue === 2) && (
          <Card>
            <CardTitle title={titleToShow()} icon="group" />
            {tabValue === 0 && (
              <MDBox px={3} pt={2} pb={1} display="flex" gap={2} flexWrap="wrap">
                <MDBox width="300px">
                  <MDInput
                    select
                    fullWidth
                    label={t("project")}
                    value={selectedProjectId}
                    onChange={(event) => setSelectedProjectId(event.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (selected === "" && !hasSingleProject) {
                          return t("allProjects");
                        }

                        const selectedProject = projectOptions.find(
                          (project) => project?.uid === selected
                        );

                        return selectedProject?.name || projectOptions[0]?.name || selected;
                      },
                    }}
                    sx={selectSx}
                  >
                    {!hasSingleProject && (
                      <MenuItem value="">
                        <em>{t("allProjects")}</em>
                      </MenuItem>
                    )}

                    {projectOptions.map((project) => (
                      <MenuItem key={project.uid} value={project.uid}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </MDInput>
                </MDBox>

                <MDBox width="300px">
                  <MDInput
                    fullWidth
                    type="datetime-local"
                    label={t("startDate")}
                    value={selectedStartDateTime}
                    onChange={(event) => setSelectedStartDateTime(event.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </MDBox>

                <MDBox width="300px">
                  <MDInput
                    fullWidth
                    type="datetime-local"
                    label={t("endDate")}
                    value={selectedEndDateTime}
                    onChange={(event) => setSelectedEndDateTime(event.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </MDBox>
              </MDBox>
            )}
            {tabValue === 1 && (
              <MDBox px={3} pt={2} pb={1} display="flex" gap={2} flexWrap="wrap">
                <MDBox width="300px">
                  <MDInput
                    select
                    fullWidth
                    label={t("project")}
                    value={selectedProjectId}
                    onChange={(event) => setSelectedProjectId(event.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (selected === "" && !hasSingleProject) {
                          return t("allProjects");
                        }

                        const selectedProject = projectOptions.find(
                          (project) => project?.uid === selected
                        );

                        return selectedProject?.name || projectOptions[0]?.name || selected;
                      },
                    }}
                    sx={selectSx}
                  >
                    {!hasSingleProject && (
                      <MenuItem value="">
                        <em>{t("allProjects")}</em>
                      </MenuItem>
                    )}

                    {projectOptions.map((project) => (
                      <MenuItem key={project.uid} value={project.uid}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </MDInput>
                </MDBox>

                <MDBox width="300px">
                  <MDInput
                    fullWidth
                    type="datetime-local"
                    label={t("date")}
                    value={selectedStartDateTime}
                    onChange={(event) => setSelectedStartDateTime(event.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </MDBox>
              </MDBox>
            )}
            <DataTable
              table={{
                columns,
                rows,
              }}
              canSearch
              canSelect={tabValue !== 0}
              canEdit={tabValue !== 0}
              canAdd={tabValue === 1 || tabValue === 2 || tabValue === 4}
              canActuate
              add={add}
              edit={edit}
              remove={remove}
              action={(data) => {
                const {
                  original: { projectId, apartmentId },
                } = data;

                if (tabValue === 0) {
                  if (!selectedProjectId && projectId) {
                    setSelectedProjectId(projectId);
                  }

                  setUids([]);
                  setCreatingClientFromReservation(false);
                  setApartmentIdSelected(apartmentId || "");
                  setActionType("add");
                  setOpen(true);
                  return;
                }

                if (projectId && apartmentId) {
                  if (Object.hasOwnProperty.call(apartments, apartmentId)) {
                    const { onnaId } = apartments[apartmentId];
                    navigate(`/projects/${projectId}/apartment/${onnaId}/${apartmentId}`);
                  }
                }
              }}
              actionIcon="arrow_forward_ios"
              actionColor="info"
            />
          </Card>
        )}
        {tabValue === 3 && <ClientReservationsTimeline />}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

ClientReservations.defaultProps = {
  clientReservations: [],
  clients: {},
  projects: {},
  apartments: {},
};

ClientReservations.propTypes = {
  clientReservations: PropTypes.array,
  clients: PropTypes.object,
  projects: PropTypes.object,
  apartments: PropTypes.object,
  actUpsertClientReservation: PropTypes.func.isRequired,
  actRemoveClientReservation: PropTypes.func.isRequired,
  actUpsertClient: PropTypes.func.isRequired,
  actSendRemoveClient: PropTypes.func.isRequired,
  actChangeClientReservationClient: PropTypes.func.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
};

AlertDialog.defaultProps = {
  handleAgree: () => {},
};

AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAgree: PropTypes.func,
  type: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => {
  const clientReservationsReducer = state?.clientReservationsReducer?.clientReservations || {};
  const isProjectManager = state?.mainUserReducer?.isProjectManager || false;
  const userProjects = state?.mainUserReducer?.projects || {};
  const clients = state?.clientsReducer || {};
  const apartments = state?.apartmentsReducer || {};

  const allClientReservations = Object.values(clientReservationsReducer).flatMap((item) =>
    Object.values(item || {})
  );

  let clientReservations = allClientReservations;

  if (isProjectManager) {
    clientReservations = allClientReservations.filter(
      (reservation) => userProjects?.[reservation?.projectId]
    );
  }
  return {
    clientReservations,
    clients,
    projects: state?.projectsReducer || {},
    apartments,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actUpsertClient: (clientData, cb) => {
    dispatch(
      upsertClient({
        clientData,
        cb,
      })
    );
  },

  actNewAlertMessage: (message) => {
    dispatch(newAlertMessage(message));
  },

  actSendRemoveClient: (uid, cb) => {
    dispatch(removeClient({ uid, cb }));
  },

  actUpsertClientReservation: (clientReservationData, cb) => {
    dispatch(upsertClientReservation({ clientReservationData, cb }));
  },

  actRemoveClientReservation: ({ clientId, uid }, cb) => {
    dispatch(removeClientReservation({ clientId, uid, cb }));
  },

  actChangeClientReservationClient: (clientId) => {
    dispatch(changeFormValue("ClientReservationForm", "clientId", clientId));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientReservations);
